import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Adds `is-visible` to elements with .reveal as they scroll into view.
 * Re-runs on every route change so new pages' .reveal elements are observed.
 */
export function useReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.is-visible)");
    if (els.length === 0) return;

    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -5% 0px" }
    );

    els.forEach((el) => io.observe(el));

    // Safety net: force-reveal anything still hidden after 1.2s.
    const fallback = setTimeout(() => {
      document
        .querySelectorAll(".reveal:not(.is-visible)")
        .forEach((el) => el.classList.add("is-visible"));
    }, 1200);

    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, [pathname]);
}
